import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VmManagementService, VirtualMachine } from '../../../services/vm-management.service';
import { ProxmoxService } from '../../../services/proxmox.service';

@Component({
  selector: 'app-vm-details',
  templateUrl: './vm-details.component.html',
  styleUrls: ['./vm-details.component.scss']
})
export class VmDetailsComponent implements OnInit {
  vm: VirtualMachine | undefined;
  vmStatus: any = null;
  vmConfig: any = null;
  vmNetwork: any[] = [];
  loading: boolean = false;
  error: string = '';
  successMessage: string = '';
  vmId: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private vmService: VmManagementService,
    private proxmoxService: ProxmoxService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.vmId = params['id'];
      this.loadVMData();
    });
  }

  loadVMData(): void {
    this.loading = true;
    this.error = '';
    this.successMessage = '';

    this.vmService.getVMById(this.vmId).subscribe({
      next: (vm) => {
        this.vm = vm;
        this.loading = false;
        
        // If we have VM data, fetch additional Proxmox information
        if (vm) {
          this.loadProxmoxVMData(vm);
        }
      },
      error: (error) => {
        console.error('Error loading VM:', error);
        this.error = 'Error loading VM details. Please try again.';
        this.loading = false;
      }
    });
  }

  loadProxmoxVMData(vm: VirtualMachine): void {
    // Extract VM ID number (e.g., vm-100 -> 100)
    const vmNumber = parseInt(vm.id.split('-')[1]);
    
    // Fetch VM status from Proxmox API
    this.proxmoxService.getVMStatus(vm.node, vm.type, vmNumber).subscribe({
      next: (response) => {
        this.vmStatus = response.data || null;
      },
      error: (error) => {
        console.error('Error loading VM status:', error);
      }
    });
    
    // Fetch VM configuration from Proxmox API
    this.proxmoxService.getVMConfig(vm.node, vm.type, vmNumber).subscribe({
      next: (response) => {
        this.vmConfig = response.data || null;
      },
      error: (error) => {
        console.error('Error loading VM config:', error);
      }
    });
    
    // For network information, we'll extract it from the status data
    // In a real implementation, you might fetch this from a separate endpoint
  }

  onStartVM(): void {
    if (this.vm) {
      this.vmService.startVM(this.vm.id).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = response.message;
            this.loadVMData(); // Refresh data
            setTimeout(() => this.successMessage = '', 3000);
          } else {
            this.error = response.message;
          }
        },
        error: (error) => {
          console.error('Error starting VM:', error);
          this.error = 'Error starting VM. Please try again.';
        }
      });
    }
  }

  onStopVM(): void {
    if (this.vm) {
      this.vmService.stopVM(this.vm.id).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = response.message;
            this.loadVMData(); // Refresh data
            setTimeout(() => this.successMessage = '', 3000);
          } else {
            this.error = response.message;
          }
        },
        error: (error) => {
          console.error('Error stopping VM:', error);
          this.error = 'Error stopping VM. Please try again.';
        }
      });
    }
  }

  onRestartVM(): void {
    if (this.vm) {
      this.vmService.restartVM(this.vm.id).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = response.message;
            this.loadVMData(); // Refresh data
            setTimeout(() => this.successMessage = '', 3000);
          } else {
            this.error = response.message;
          }
        },
        error: (error) => {
          console.error('Error restarting VM:', error);
          this.error = 'Error restarting VM. Please try again.';
        }
      });
    }
  }

  onPauseVM(): void {
    if (this.vm) {
      this.vmService.pauseVM(this.vm.id).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = response.message;
            this.loadVMData(); // Refresh data
            setTimeout(() => this.successMessage = '', 3000);
          } else {
            this.error = response.message;
          }
        },
        error: (error) => {
          console.error('Error pausing VM:', error);
          this.error = 'Error pausing VM. Please try again.';
        }
      });
    }
  }

  onResumeVM(): void {
    if (this.vm) {
      this.vmService.resumeVM(this.vm.id).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = response.message;
            this.loadVMData(); // Refresh data
            setTimeout(() => this.successMessage = '', 3000);
          } else {
            this.error = response.message;
          }
        },
        error: (error) => {
          console.error('Error resuming VM:', error);
          this.error = 'Error resuming VM. Please try again.';
        }
      });
    }
  }

  onDeleteVM(): void {
    if (this.vm && confirm(`Are you sure you want to delete VM "${this.vm.name}"? This action cannot be undone.`)) {
      this.vmService.deleteVM(this.vm.id).subscribe({
        next: (success) => {
          if (success) {
            this.successMessage = 'VM deleted successfully.';
            setTimeout(() => {
              this.router.navigate(['/client-dashboard']); // Redirect to dashboard
            }, 2000);
          } else {
            this.error = 'Error deleting VM. Please try again.';
          }
        },
        error: (error) => {
          console.error('Error deleting VM:', error);
          this.error = 'Error deleting VM. Please try again.';
        }
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/client-dashboard']);
  }

  getVMStatusClass(status: string): string {
    switch (status) {
      case 'running':
        return 'badge-success';
      case 'stopped':
        return 'badge-danger';
      case 'paused':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  }
}